import React from 'react'
// libs
import styled from 'styled-components'
import { useRouter } from 'hooks/useRouter'
import PerfectScrollbar from 'react-perfect-scrollbar'
// queries
import { RISKS_QUERY } from 'graphql/risk/queries'
// hooks
import { useApolloClient } from 'hooks/useApolloClient'
import { useRiskRelatedForms } from 'hooks/risks'
// components
import { Button, BUTTON_LAYOUTS, CREATE_BUTTON_STYLE } from 'components/UI'
// context
import { RiskAPIContext } from 'containers/Risks/Risk/Edit/layers/API'
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// types
import { IRisk } from 'types/graphql'
// utils
import { get } from 'lodash'
import { parseGraphQLErrorMessages } from 'utils/errors/parsers'

const RelatedFormsContainer = styled<any>(PerfectScrollbar)`
  height: auto;
  display: flex;
  gap: 12px;
  padding-bottom: 20px;

  button {
    max-width: unset !important;
  }
`

interface Props {
  parentRisk?: IRisk
}

const RelatedForms: React.FC<Props> = ({ parentRisk }) => {
  const router = useRouter()

  const { client } = useApolloClient()

  const { risk, createRelatedRisk } = React.useContext(RiskAPIContext)
  const { riskTemplate } = React.useContext(RiskTemplateAPIContext)

  const [isLoading, setIsLoading] = React.useState(false)

  const { combinedForms, parentForms, ownForms, parentRelatedForm } = useRiskRelatedForms()

  // Which set the current record itself belongs to — used to pick the highlighted tab when the
  // same template appears in both sets.
  const activeOwnerId = parentRelatedForm?.ownerId ?? risk?.id

  // With both sets on screen at once, two tabs can carry the same template name and lead to
  // different records, so each one is prefixed with the record that owns it.
  const showOwnerPrefix = !!parentForms.length && !!ownForms.length

  const ownerTemplateOf = (relatedForm) =>
    relatedForm.ownerId === risk?.id ? risk?.riskTemplate : parentRisk?.riskTemplate

  const getLabel = (relatedForm) => {
    const ownerTemplateName = ownerTemplateOf(relatedForm)?.name

    return showOwnerPrefix && ownerTemplateName
      ? `${ownerTemplateName}: ${relatedForm.riskTemplate.name}`
      : relatedForm.riskTemplate.name
  }

  // Every route but the multiple one carries the hierarchy on the record itself, so no transition
  // from here needs to put the parent into the URL.
  const navigateToParentRisk = () => {
    router.push(`/forms/edit/${parentRisk.id}/template/${parentRisk.riskTemplate.id}`)
  }

  const navigateToRelatedRisk = (relatedForm) => async () => {
    try {
      if (relatedForm.riskTemplateId === router.query.templateId) {
        return
      }

      setIsLoading(true)

      let existingRelatedRisk

      if (!relatedForm.multiple) {
        const existingRelatedRisks = get(
          await client.query({
            fetchPolicy: 'network-only',
            query: RISKS_QUERY,
            variables: {
              filter: {
                parentRiskId: relatedForm.ownerId,
                riskTemplateId: relatedForm.riskTemplateId
              },
              pagination: { pageTake: 1000, pageSkip: 0 }
            }
          }),
          'data.risks.data',
          []
        )

        existingRelatedRisk = existingRelatedRisks.find(
          ({ riskTemplate }) => riskTemplate.id === relatedForm.riskTemplateId
        )
      }

      if (!relatedForm.multiple && !existingRelatedRisk) {
        await createRelatedRisk(relatedForm)
      } else {
        router.push({
          pathname: relatedForm.multiple
            ? `/forms/edit/template/${relatedForm.riskTemplateId}`
            : `/forms/edit/${existingRelatedRisk.id}/template/${relatedForm.riskTemplateId}`,
          // The multiple route has no record of its own, so it is the one place that still needs
          // the owner of the set spelled out in the URL — both its id and its template, since
          // resolving the parent takes the template too.
          query: relatedForm.multiple
            ? {
                multiple: 'true',
                parentRiskId: relatedForm.ownerId,
                parentTemplateId: ownerTemplateOf(relatedForm)?.id
              }
            : {}
        })
      }
    } catch (e) {
      console.log('[RelatedForms.navigateToRelatedRisk]:', parseGraphQLErrorMessages(e))
    } finally {
      setIsLoading(false)
    }
  }

  if (!combinedForms.length && !parentRisk) {
    return null
  }

  return (
    <RelatedFormsContainer options={{ suppressScrollY: true }}>
      {!!parentRisk && (
        <Button
          data-testid='related-form-parent'
          data-entity-id={parentRisk.id}
          style={CREATE_BUTTON_STYLE}
          onClick={navigateToParentRisk}
          layout={BUTTON_LAYOUTS.PURPLE_INVERTED}>
          {parentRisk.riskTemplate.name}
        </Button>
      )}
      {combinedForms.map((relatedForm) => (
        <Button
          key={`${relatedForm.ownerId}:${relatedForm.riskTemplateId}`}
          data-testid='related-form-tab'
          data-entity-id={relatedForm.riskTemplateId}
          data-owner-id={relatedForm.ownerId}
          style={CREATE_BUTTON_STYLE}
          onClick={navigateToRelatedRisk(relatedForm)}
          disabled={isLoading || (!relatedForm.multiple && !relatedForm.riskId && !relatedForm.permissions.riskCreate)}
          layout={
            riskTemplate.id === relatedForm.riskTemplateId && relatedForm.ownerId === activeOwnerId
              ? BUTTON_LAYOUTS.PURPLE
              : BUTTON_LAYOUTS.PURPLE_INVERTED
          }>
          {getLabel(relatedForm)}
        </Button>
      ))}
    </RelatedFormsContainer>
  )
}

export default RelatedForms
